
import React, { useState, useEffect } from 'react';
import { FinancialParams } from '../types';
import { Calculator, Coins, CalendarClock, TrendingUp, ArrowRight, DollarSign, Percent, Briefcase, TrendingUp as TrendingIcon, FileText } from 'lucide-react';

interface Props {
  onSubmit: (data: FinancialParams) => void;
  defaultArea: string; // Passed from project data to help user estimate
}

export const FinancialParamsForm: React.FC<Props> = ({ onSubmit, defaultArea }) => {
  // Parse area to number for estimation
  const areaNum = parseFloat(defaultArea) || 0;

  const [params, setParams] = useState<FinancialParams>({
    capexPerSqm: '2500',
    leaseModel: 'fixed',
    leaseUnitValue: '',
    rentFreePeriod: '3',
    leaseTerm: '10',
    rentEscalation: '每2年递增2%',
    revenueGrowthRate: '首年不涨，之后每2年递增1%', // Default value
    revenueShare: '5', // Default 5%
    noiShare: '10',    // Default 10%
    customFinancials: ''
  });

  const [estimatedMonthlyRent, setEstimatedMonthlyRent] = useState<string>('0');

  // Update estimation whenever params change
  useEffect(() => {
    if (params.leaseModel === 'fixed' && params.leaseUnitValue && areaNum > 0) {
        const total = parseFloat(params.leaseUnitValue) * areaNum;
        setEstimatedMonthlyRent((total / 10000).toFixed(2) + ' 万');
    } else {
        setEstimatedMonthlyRent('N/A (按营收比例)');
    }
  }, [params.leaseModel, params.leaseUnitValue, areaNum]);

  // Handle switching to Light Asset (Zero Capex)
  const handleModeChange = (mode: 'fixed' | 'ratio' | 'light_asset') => {
      if (mode === 'light_asset') {
          setParams(prev => ({ ...prev, leaseModel: mode, capexPerSqm: '0' }));
      } else {
          // Restore default capex if switching back from light asset (optional UX choice)
          setParams(prev => ({ ...prev, leaseModel: mode, capexPerSqm: prev.capexPerSqm === '0' ? '2500' : prev.capexPerSqm }));
      }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(params);
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-5 md:p-8 rounded-xl shadow-xl border border-blue-100 animate-fade-in-up my-8">
      <div className="text-center mb-6 md:mb-8">
        <div className="w-12 h-12 md:w-16 md:h-16 bg-blue-50 rounded-full mx-auto flex items-center justify-center mb-4">
          <Calculator className="w-6 h-6 md:w-8 md:h-8 text-blue-600" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-slate-900">投资测算参数设定</h2>
        <p className="text-sm md:text-base text-slate-500 mt-2">AI 将基于您设定的商务条件，计算 IRR 与 回本周期</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
        
        {/* Mode Selection */}
        <div className="bg-slate-50 p-4 md:p-5 rounded-lg border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-3 md:mb-4 flex items-center text-sm md:text-base">
                <Briefcase className="w-4 h-4 md:w-5 md:h-5 mr-2 text-blue-600" />
                商业合作模式
            </h3>
            <div className="flex space-x-2">
                <button
                    type="button"
                    onClick={() => handleModeChange('fixed')}
                    className={`flex-1 py-2 md:py-3 px-1 md:px-2 rounded-lg border flex flex-col items-center justify-center transition text-xs md:text-sm font-medium ${params.leaseModel === 'fixed' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                    <div className="flex items-center mb-1"><DollarSign className="w-3 h-3 md:w-4 md:h-4 mr-1" /> <span className="whitespace-nowrap">包租(固定)</span></div>
                </button>
                <button
                    type="button"
                    onClick={() => handleModeChange('ratio')}
                    className={`flex-1 py-2 md:py-3 px-1 md:px-2 rounded-lg border flex flex-col items-center justify-center transition text-xs md:text-sm font-medium ${params.leaseModel === 'ratio' ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                    <div className="flex items-center mb-1"><Percent className="w-3 h-3 md:w-4 md:h-4 mr-1" /> <span className="whitespace-nowrap">包租(分成)</span></div>
                </button>
                <button
                    type="button"
                    onClick={() => handleModeChange('light_asset')}
                    className={`flex-1 py-2 md:py-3 px-1 md:px-2 rounded-lg border flex flex-col items-center justify-center transition text-xs md:text-sm font-medium ${params.leaseModel === 'light_asset' ? 'bg-green-600 text-white border-green-600 shadow-md' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                >
                    <div className="flex items-center mb-1"><Briefcase className="w-3 h-3 md:w-4 md:h-4 mr-1" /> <span className="whitespace-nowrap">轻资产委托</span></div>
                </button>
            </div>
        </div>

        {/* Global Growth Assumptions */}
        <div className="bg-slate-50 p-4 md:p-5 rounded-lg border border-slate-200">
             <h3 className="font-bold text-slate-800 mb-3 md:mb-4 flex items-center text-sm md:text-base">
                <TrendingIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 text-green-600" />
                营收增长假设 (Revenue Growth)
            </h3>
            <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    租金/营收年增长率
                </label>
                <input
                    type="text"
                    required
                    className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                    value={params.revenueGrowthRate}
                    onChange={(e) => setParams({ ...params, revenueGrowthRate: e.target.value })}
                    placeholder="例如：首年不涨，之后每2年递增1%"
                />
                <p className="text-xs text-gray-500 mt-1">* 将直接决定 GOP 增长曲线，请勿使用过高假设</p>
            </div>
        </div>

        {/* CAPEX Section */}
        <div className={`p-4 md:p-5 rounded-lg border transition-all ${params.leaseModel === 'light_asset' ? 'bg-gray-100 border-gray-200 opacity-70' : 'bg-slate-50 border-slate-200'}`}>
          <h3 className="font-bold text-slate-800 mb-3 md:mb-4 flex items-center text-sm md:text-base">
            <Coins className="w-4 h-4 md:w-5 md:h-5 mr-2 text-orange-500" />
            改造投资投入 (CAPEX)
          </h3>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
              单方改造标准 (元/㎡)
            </label>
            <input
              type="number"
              required
              disabled={params.leaseModel === 'light_asset'}
              className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:bg-gray-200 disabled:text-gray-500 text-sm md:text-base"
              value={params.capexPerSqm}
              onChange={(e) => setParams({ ...params, capexPerSqm: e.target.value })}
            />
            <p className="text-xs text-gray-500 mt-1">
              {params.leaseModel === 'light_asset' 
                ? "* 轻资产模式下，默认无硬装投入（纯服务输出）"
                : `* 按总面积 ${areaNum}㎡ 预估，总投资约 ${(parseFloat(params.capexPerSqm || '0') * areaNum / 10000).toFixed(1)} 万`
              }
            </p>
          </div>
        </div>

        {/* Master Lease / Fee Section */}
        <div className="bg-slate-50 p-4 md:p-5 rounded-lg border border-slate-200">
          <h3 className="font-bold text-slate-800 mb-3 md:mb-4 flex items-center text-sm md:text-base">
            <CalendarClock className="w-4 h-4 md:w-5 md:h-5 mr-2 text-blue-600" />
            {params.leaseModel === 'light_asset' ? '管理费收入标准 (Management Fee)' : '底租成本标准 (Master Lease)'}
          </h3>
          
          {params.leaseModel === 'light_asset' ? (
              // LIGHT ASSET INPUTS
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            营收分成比例 (X%)
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                required
                                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                                value={params.revenueShare}
                                onChange={(e) => setParams({ ...params, revenueShare: e.target.value })}
                                placeholder="5"
                            />
                            <span className="absolute right-3 top-3 text-gray-400 text-sm">% of GPR</span>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                            NOI 分成比例 (Y%)
                        </label>
                        <div className="relative">
                            <input
                                type="number"
                                required
                                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                                value={params.noiShare}
                                onChange={(e) => setParams({ ...params, noiShare: e.target.value })}
                                placeholder="10"
                            />
                            <span className="absolute right-3 top-3 text-gray-400 text-sm">% of NOI</span>
                        </div>
                    </div>
                </div>
                <div className="mt-4">
                     <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        委托管理/合作年限 (年)
                    </label>
                    <input
                        type="number"
                        required
                        className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 text-sm md:text-base"
                        value={params.leaseTerm}
                        onChange={(e) => setParams({ ...params, leaseTerm: e.target.value })}
                        placeholder="例如：10"
                    />
                </div>
              </>
          ) : (
              // HEAVY ASSET (LEASE) INPUTS
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        {params.leaseModel === 'fixed' ? '底租单价 (元/㎡/月)' : '分成比例 (%)'}
                    </label>
                    <input
                        type="number"
                        required
                        className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        value={params.leaseUnitValue}
                        onChange={(e) => setParams({ ...params, leaseUnitValue: e.target.value })}
                        placeholder={params.leaseModel === 'fixed' ? "例如：45" : "例如：15"}
                    />
                    {params.leaseModel === 'fixed' && (
                        <p className="text-xs text-gray-500 mt-1">预估月总租金: {estimatedMonthlyRent}</p>
                    )}
                    </div>
                    <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        底租递增条款
                    </label>
                    <input
                        type="text"
                        required
                        className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        value={params.rentEscalation}
                        onChange={(e) => setParams({ ...params, rentEscalation: e.target.value })}
                        placeholder="例如：每2年递增2%"
                    />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        免租期 (月)
                    </label>
                    <input
                        type="number"
                        required
                        className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        value={params.rentFreePeriod}
                        onChange={(e) => setParams({ ...params, rentFreePeriod: e.target.value })}
                    />
                    </div>
                    <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                        租期/合同期 (年)
                    </label>
                    <input
                        type="number"
                        required
                        className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm md:text-base"
                        value={params.leaseTerm}
                        onChange={(e) => setParams({ ...params, leaseTerm: e.target.value })}
                    />
                    </div>
                </div>
              </>
          )}
        </div>

        {/* Custom Financial Info */}
        <div className="bg-slate-50 p-4 md:p-5 rounded-lg border border-slate-200">
             <h3 className="font-bold text-slate-800 mb-3 md:mb-4 flex items-center text-sm md:text-base">
                <FileText className="w-4 h-4 md:w-5 md:h-5 mr-2 text-purple-600" />
                其他财务/税务/融资补充 (选填)
            </h3>
            <textarea
                className="w-full p-2.5 md:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 h-20 md:h-24 text-sm"
                placeholder="例如：贷款金额500万，年利率4.5%，等额本息；或者企业所得税按小微企业计算..."
                value={params.customFinancials || ''}
                onChange={(e) => setParams({ ...params, customFinancials: e.target.value })}
            />
        </div>

        <button
          type="submit"
          className="w-full bg-slate-900 text-white py-3 md:py-4 rounded-xl font-bold text-base md:text-lg hover:bg-slate-800 transition shadow-lg flex items-center justify-center transform hover:scale-[1.01]"
        >
          <TrendingUp className="w-4 h-4 md:w-5 md:h-5 mr-2" />
          {params.leaseModel === 'light_asset' ? '生成轻资产收益测算' : '生成 IRR 投资测算表'}
          <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
        </button>
      </form>
    </div>
  );
};
